import { useMutation } from '@tanstack/react-query';
import { useAccount, useConfig } from 'wagmi';
import { getCancelOrderBody } from '../components/OpenOrdersTable/cancelOrder';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { isUserRejectedError } from '../lib/errors';
import { captureError } from '../lib/errorTracking';
import {
  CancelOrderError,
  getOrderCancelErrorMessage,
  ORDER_SUCCESS_RESULT,
} from '../lib/orderError';
import { showToast } from '../lib/showToast';
import {
  cancelRejectedWithReasonToastMessage,
  orderCancellationFailedToastMessage,
  orderCancellationPartialSuccessToastMessage,
  orderCancellationSuccessToastMessage,
} from '../lib/toasts';
import { assertRequiredData } from '../lib/typeGuards';
import { useCancelOrderStore } from './useCancelledOrdersStore';
import { useDataForSigning } from './useDataForSigning';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { useWalletStorage } from './useWalletStorage';

export const useOrderCancelMutation = (options?: {
  showToast: boolean;
  optimisticCancel: boolean;
}) => {
  const optionsWithDefault = { showToast: true, optimisticCancel: true, ...options };

  const { getSubaccountData } = useWalletStorage();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { address } = useAccount();
  const { signedTypedDataTypes, domain } = useDataForSigning();
  const subaccountData = getSubaccountData(address, selectedSubaccount?.id);
  const config = useConfig();
  const setOrderCancelled = useCancelOrderStore((s) => s.setOrder);
  const removeOrderCancelled = useCancelOrderStore((s) => s.removeOrder);

  return useMutation({
    mutationKey: ['cancelOrder'],
    mutationFn: async (orderIds: string[]) => {
      const params = {
        orderIds,
        selectedSubaccount: selectedSubaccount?.name,
        address,
        domain,
        signedTypedDataTypes,
        config,
      };
      assertRequiredData(params);

      const { data } = await fetchClient
        .POST('/v1/order/cancel', {
          body: await getCancelOrderBody({ ...params, subaccountData }),
        })
        .then(dataOrThrow);
      // TODO, handle failure reason, the api returns 200 with failure reason
      return { cancelResponse: data.data, orderIds };
    },
    async onSuccess({ cancelResponse }) {
      const successfulOrders = cancelResponse.filter(
        ({ result }) => result === ORDER_SUCCESS_RESULT
      );
      const failedOrders = cancelResponse.filter(
        (order): order is { id: string; result: CancelOrderError } =>
          order.result !== ORDER_SUCCESS_RESULT
      );

      if (optionsWithDefault.optimisticCancel) {
        // We use this store for optimistically updating cancel orders.
        successfulOrders.forEach(({ id }) => setOrderCancelled(id));
      }

      if (!optionsWithDefault.showToast) return;

      if (!failedOrders.length) {
        // All orders successfully canceled
        showToast(orderCancellationSuccessToastMessage(cancelResponse.length));
        return;
      }

      // Single order with error
      if (cancelResponse.length === 1) {
        const errorMessage = getOrderCancelErrorMessage(failedOrders[0]?.result);
        showToast(cancelRejectedWithReasonToastMessage(errorMessage));
        return;
      }

      // Multiple orders with some failures
      showToast(
        orderCancellationPartialSuccessToastMessage(successfulOrders.length, failedOrders.length)
      );
    },
    onError: (error, orderIds) => {
      if (isUserRejectedError(error)) return;
      // Revert optimistic update
      orderIds.forEach((orderId) => removeOrderCancelled(orderId));

      captureError(error);
      showToast(orderCancellationFailedToastMessage());
    },
  });
};
